import * as React from "react";

import { resolveAnalyticsElement } from "@/lib/analytics/analyticsElement";
import type { AnalyticsElementId } from "@/lib/analytics/analyticsElementId";
import { cn } from "@/lib/utils";

const useIsomorphicLayoutEffect = typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

export type AccordionItemConfig = {
  id?: string;
  title: React.ReactNode;
  content: React.ReactNode;
  defaultOpen?: boolean;
  collapsedIcon?: React.ReactNode;
  expandedIcon?: React.ReactNode;
  triggerClassName?: string;
  titleProps?: React.HTMLAttributes<HTMLSpanElement>;
  contentWrapperClassName?: string;
  contentInnerClassName?: string;
  /**
   * Structured element id wiring click analytics for the item's trigger: stamps the
   * `data-analytics-*` marker attributes and queues `fe_element_clicked` on toggle.
   */
  analyticsElementId?: AnalyticsElementId;
  itemProps?: React.HTMLAttributes<HTMLElement>;
  contentWrapperProps?: React.HTMLAttributes<HTMLDivElement>;
  contentInnerProps?: React.HTMLAttributes<HTMLDivElement>;
};

type AccordionToggleDetail = {
  index: number;
  item: AccordionItemConfig;
  nextState: boolean;
};

type AccordionProps = {
  items: AccordionItemConfig[];
  className?: string;
  multiple?: boolean;
  showDividers?: boolean;
  dividerClassName?: string;
  onItemToggle?: (detail: AccordionToggleDetail) => void;
};

export function Accordion({
  items,
  className,
  multiple = true,
  showDividers = false,
  dividerClassName,
  onItemToggle,
}: AccordionProps) {
  const [openStates, setOpenStates] = React.useState(() => items.map((item) => Boolean(item.defaultOpen)));

  // Re-seed the open states when a different items array is passed
  // (render-phase adjustment instead of a post-commit effect).
  const [prevItems, setPrevItems] = React.useState(items);
  if (items !== prevItems) {
    setPrevItems(items);
    setOpenStates(items.map((item) => Boolean(item.defaultOpen)));
  }

  const handleToggle = React.useCallback(
    (index: number) => {
      setOpenStates((prev) => {
        const next = [...prev];
        const nextValue = !prev[index];

        if (!multiple) {
          next.fill(false);
        }

        next[index] = nextValue;

        onItemToggle?.({ index, item: items[index], nextState: nextValue });
        return next;
      });
    },
    [items, multiple, onItemToggle],
  );

  return (
    <div className={cn("flex flex-col", className)}>
      {items.map((item, index) => {
        const isOpen = openStates[index] ?? false;
        const key = item.id ?? `accordion-item-${index}`;

        return (
          <React.Fragment key={key}>
            <AccordionItem {...item} isOpen={isOpen} onToggle={() => handleToggle(index)} />
            {showDividers && index < items.length - 1 ? (
              <div className={cn("my-1 h-px w-full bg-(--color-border)", dividerClassName)} />
            ) : null}
          </React.Fragment>
        );
      })}
    </div>
  );
}

type InternalAccordionItemProps = AccordionItemConfig & {
  isOpen: boolean;
  onToggle: () => void;
};

function AccordionItem({
  title,
  content,
  collapsedIcon,
  expandedIcon,
  triggerClassName,
  contentWrapperClassName,
  contentInnerClassName,
  analyticsElementId,
  itemProps,
  contentWrapperProps,
  contentInnerProps,
  titleProps,
  isOpen,
  onToggle,
}: InternalAccordionItemProps) {
  const contentId = React.useId();
  const contentRef = React.useRef<HTMLDivElement>(null);

  const { className: itemClassName, ...restItemProps } = itemProps ?? {};
  const { className: wrapperClassName, style: wrapperStyle, ...restWrapperProps } = contentWrapperProps ?? {};
  const { className: innerClassName, ...restInnerProps } = contentInnerProps ?? {};
  const { className: titleClassName, ...restTitleProps } = titleProps ?? {};

  useIsomorphicLayoutEffect(() => {
    const node = contentRef.current;
    if (!node) {
      return;
    }

    let openResetTimeout: number | undefined;

    if (isOpen) {
      node.style.maxHeight = `${node.scrollHeight}px`;
      openResetTimeout = window.setTimeout(() => {
        if (isOpen && contentRef.current === node) {
          node.style.maxHeight = "none";
        }
      }, 350);
    } else {
      if (node.style.maxHeight === "none") {
        node.style.maxHeight = `${node.scrollHeight}px`;
        requestAnimationFrame(() => {
          if (contentRef.current === node) {
            node.style.maxHeight = "0px";
          }
        });
      } else {
        node.style.maxHeight = "0px";
      }
    }

    const handleTransitionEnd = () => {
      if (isOpen) {
        node.style.maxHeight = "none";
      }
    };

    node.addEventListener("transitionend", handleTransitionEnd);
    return () => {
      node.removeEventListener("transitionend", handleTransitionEnd);
      if (openResetTimeout) {
        window.clearTimeout(openResetTimeout);
      }
    };
  }, [isOpen]);

  const icon = isOpen ? expandedIcon : collapsedIcon;
  const analyticsElement = resolveAnalyticsElement(analyticsElementId);

  const handleToggleClick = () => {
    analyticsElement?.emitClick();
    onToggle();
  };

  return (
    <article {...restItemProps} className={cn("w-full", itemClassName)}>
      <button
        type="button"
        onClick={analyticsElement ? handleToggleClick : onToggle}
        className={cn("flex w-full items-start gap-2 py-4 text-left", triggerClassName)}
        aria-expanded={isOpen}
        aria-controls={contentId}
        {...analyticsElement?.attributes}
      >
        <span className={cn("flex-1 text-(--color-text-primary)", titleClassName)} {...restTitleProps}>
          {title}
        </span>
        {icon ? <span className="inline-flex shrink-0 items-center justify-center">{icon}</span> : null}
      </button>
      <div
        id={contentId}
        ref={contentRef}
        className={cn(
          "overflow-hidden transition-all duration-300 ease-out",
          contentWrapperClassName,
          wrapperClassName,
        )}
        style={wrapperStyle}
        aria-hidden={!isOpen}
        // A collapsed panel stays mounted at `max-height: 0`, and neither that nor `aria-hidden`
        // takes its contents out of the tab order — so any link inside one would still catch
        // keyboard focus while invisible. `inert` is what actually removes the subtree.
        inert={!isOpen}
        {...restWrapperProps}
      >
        <div
          className={cn(
            "text-body-md w-full pb-4 text-(--color-text-secondary)",
            contentInnerClassName,
            innerClassName,
          )}
          {...restInnerProps}
        >
          {content}
        </div>
      </div>
    </article>
  );
}
